// TODO: remove polling functionality and move to a usePolling hook/context
import React, { useState, useEffect, useContext, createContext, useRef } from "react"
import { useNavigate } from "react-router-dom"
import useToastNotifications from "utilities/useToastNotifications"
import useRefreshToken from "utilities/useRefreshToken"
import APP_STATE from "constants/appState.js"

// apis
import { usePollTask } from "api/hooks"
import getUrl from "api/apiMap"
import apiEndpoints from "api/utils/apiEndpoints"
import useAxiosPrivate from "../../api/hooks/useAxiosPrivate"

const AppStateContext = createContext({})

export const AppStateProvider = ({ children }) => {
  const navigate = useNavigate()
  const axios = useAxiosPrivate()

  const [blurLoading, setBlurLoading] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(localStorage.getItem("SIDEBAR_STATE") === "true" ? true : false)
  const [publicEnvVars, setPublicEnvVars] = useState("3.0")
  const [isMounted, setIsMounted] = useState(false)
  const { pathnameRef, isNextUrlValid } = useRefreshToken()

  // request/study (un)favoriting
  const [studyFaveUpdated, setStudyFaveUpdated] = useState(false)
  const emitStudyFaveUpdated = () => setStudyFaveUpdated(true)
  const [requestFaveUpdated, setRequestFavorited] = useState(false)
  const emitRequestFaveUpdated = () => setRequestFavorited(true)

  // polling
  const [pollingTaskId, setPollingTaskId] = useState(null)
  const [taskType, setTaskType] = useState(null)

  const pollingCallbackRef = React.useRef(null)
  const pollingCallbackErrorRef = React.useRef(null)

  /*
    App state
      1. initializing
      2. authorizing
      3. ready
  */
  const [appState, setAppState] = useState(APP_STATE.initializing)
  const appStateRef = useRef(APP_STATE.initializing)

  const { data: pollingData } = usePollTask(pollingTaskId, {
    appState,
    refetchInterval: pollingTaskId ? 1000 : null,
    enabled: pollingTaskId ? true : false,
    isMounted,
    notifyOnChangeProps: ["data", "complete"],
  })

  const { openToastNotification, toastContextHolder } = useToastNotifications()

  useEffect(() => {
    const onVerifyPollingData = async () => {
      if (pollingData?.complete) {
        if (pollingData?.success) {
          if (pollingData?.result?.success_msg) {
            openToastNotification("success", "Success", pollingData?.result.success_msg)
          } else if (taskType?.includes("report-export")) {
            window.open(pollingData.result)
          } else {
            if (typeof pollingCallbackRef.current === "function" && pollingData) {
              pollingCallbackRef.current(pollingData.result)
              pollingCallbackRef.current = null
            }

            openToastNotification("success", "Complete", "Task Complete")
          }

          if (pollingData?.result?.error_msg) {
            openToastNotification("error", "Error", pollingData.result.error_msg)
            if (typeof pollingCallbackErrorRef.current === "function" && pollingData) {
              pollingCallbackErrorRef.current()
              pollingCallbackRef.current = null
            }
          }

          setPollingTaskId(null)
          setTaskType(null)
        } else if (pollingData?.state === "FAILURE") {
          openToastNotification("error", "Error", pollingData?.result)
          setPollingTaskId(null)
          setTaskType(null)
          if (typeof pollingCallbackErrorRef.current === "function" && pollingData) {
            pollingCallbackErrorRef.current()
            pollingCallbackRef.current = null
          }
        }
      }
    }

    onVerifyPollingData()
  }, [pollingData?.complete, taskType])

  const onCancelTask = () => {
    return axios
      .get(getUrl(apiEndpoints.cancelTask), {
        params: {
          "queue-id": pollingTaskId,
        },
      })
      .then((resp) => {
        setPollingTaskId(null)
        setTaskType(null)
        openToastNotification("info", "NOTE", "Task cancelled")
      })
  }

  const navigateToNextUrl = (options) => {
    let isSso = options?.isSso
    let nextUrl = "/my-prime"
    let ssoNext = localStorage.getItem("prime-next-url")
    if (isSso && ssoNext) {
      nextUrl = ssoNext
      localStorage.removeItem("prime-next-url")
    } else if (pathnameRef?.current && isNextUrlValid(pathnameRef.current)) {
      nextUrl = pathnameRef.current
    }

    navigate(nextUrl)

    appStateRef.current = APP_STATE.authenticated
    setAppState(APP_STATE.authenticated)
  }

  return (
    <AppStateContext.Provider
      value={{
        // body blur for loading:
        blurLoading,
        setBlurLoading,

        // side bar control:
        sidebarOpen,
        setSidebarOpen,

        // notifications:
        openToastNotification, // (type, message, description, placement, options = {})
        toastContextHolder,

        appState,
        setAppState,
        appStateRef,
        publicEnvVars,
        setPublicEnvVars,

        /**
         * (Un)favoriting studies/requests:
         *
         * Call **emitStudyFaveUpdated** after an API call that (un)favorites a study has successfully completed. Similarly, call **emitRequestFaveUpdated** for requests.
         *
         * After calling emitRequestFaveUpdated update the UI, e.g. refresh grids, by sending a callback function to **useHandleStudyFavorited**. Similarly, send a callback to **useHandleRequestFavorited** for requests.
         */
        studyFaveUpdated,
        setStudyFaveUpdated,
        emitStudyFaveUpdated,
        requestFaveUpdated,
        setRequestFavorited,
        emitRequestFaveUpdated,

        // polling:
        pollingTaskId,
        setPollingTaskId,
        taskType,
        setTaskType,
        pollingData,
        isMounted,
        setIsMounted,
        onCancelTask,
        navigateToNextUrl,

        // polling:
        pollingCallbackRef,
        pollingCallbackErrorRef,
      }}
    >
      {children}
    </AppStateContext.Provider>
  )
}

const useAppState = () => {
  return useContext(AppStateContext)
}

/**
 * After calling the app state function emitStudyFaveUpdated from one component, update the UI on another component by sending a callback function to useHandleStudyFavorited
 */
export const useHandleStudyFavorited = (callback) => {
  const { studyFaveUpdated, setStudyFaveUpdated } = useAppState()
  useEffect(() => {
    if (studyFaveUpdated) {
      if (callback) {
        callback()
        setStudyFaveUpdated(false)
      }
    }
  }, [studyFaveUpdated])
}

/**
 * After calling the app state function emitRequestFaveUpdated from one component, update the UI on another component by sending a callback function to useHandleRequestFavorited
 */
export const useHandleRequestFavorited = (callback) => {
  const { requestFaveUpdated, setRequestFavorited } = useAppState()
  useEffect(() => {
    if (requestFaveUpdated) {
      if (callback) {
        callback()
        setRequestFavorited(false)
      }
    }
  }, [requestFaveUpdated])
}

export default useAppState
